import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchHotspots,
  fetchScrubVideoBlobUrl,
  fetchScrubVideoLowBlobUrl,
} from '../api';
import type { HotspotsDocument, HotspotTrack } from '../types';
import { snapToUniformSample, uniformSampleIndex } from '../utils/videoSampling';
import { HotspotModal } from './HotspotModal';
import { HotspotOverlay } from './HotspotOverlay';

interface Viewer360Props {
  jobId: string;
  /** Used purely to build a friendly filename for "Download current frame". */
  scanId?: string;
  /** When set, drag seeks snap to this many uniform positions. */
  sampleCount?: number;
  /** False when the completed job definitively has no hotspot artifact. */
  hotspotsAvailable?: boolean;
}

/**
 * Video-based 360 turntable viewer.
 *
 * The backend's configured AV1/WebM or H.264/MP4 artifact is encoded for
 * responsive seeking, so drag-scrubbing via `<video>.currentTime` lands on
 * the requested frame.
 *
 * Performance characteristics on a long/high-resolution video are
 * dominated by three things: (1) how often we issue seeks, (2) how
 * many pixels the GPU has to redecode per seek, and (3) whether we
 * issue the next seek before the previous one was painted.
 *
 * This component addresses all three:
 *
 * - Seeks are coalesced via `requestAnimationFrame`. Pointer events
 *   only update `pendingTargetRef`; one rAF tick reads the latest
 *   target and assigns `currentTime`. The next seek is gated on the
 *   `seeked` event — we never have two seeks in flight at the same
 *   time, so the decoder never falls behind. This caps the seek rate
 *   to whatever the decoder can keep up with rather than the raw
 *   pointermove rate (often 200+ Hz on modern trackpads, which is
 *   wildly more than a 1080p decoder can do).
 *
 * - When a low-resolution scrub video is available, we render it
 *   stacked over the full-resolution element and swap visibility on
 *   pointerdown / pointerup. Decoding cost scales linearly with pixel
 *   count, so a 540p preview during drag is ~4x cheaper than the
 *   1080p source. On pointerup we sync the high-res element's
 *   `currentTime` to the low-res's and only fade back once it has
 *   actually decoded the same frame, otherwise the user would see a
 *   one-frame stale flash.
 *
 * - Pointer events with `setPointerCapture` keep the drag alive when
 *   the cursor leaves the element — no more dropped drags on fast
 *   horizontal swipes.
 *
 * Jobs from before the low-res variant existed continue to work: the
 * low-res GET returns 404, `lowResAvailable` stays false, and we drive
 * the high-res element directly throughout.
 *
 * Videos are pulled via authenticated `apiFetch` and turned into
 * blob URLs (see `web/src/video/api.ts` for the rationale). Trade-off
 * is that the user waits for the full download before the first scrub
 * — `<video src=URL>` would have streamed it via Range — but the scrub
 * video needs the full file in cache for instant seeks anyway.
 */

// Drop seeks smaller than one frame at 60fps — they do nothing
// visible and just consume decoder budget the next pointermove will
// need.
const MIN_SEEK_STEP_SECONDS = 1 / 60;

export function Viewer360({
  jobId,
  scanId,
  sampleCount,
  hotspotsAvailable,
}: Viewer360Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const lowResVideoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Mirror the high-res element into state so the hotspot overlay can
  // measure it (a plain ref wouldn't trigger a re-render on mount).
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const setVideoNode = useCallback((node: HTMLVideoElement | null) => {
    videoRef.current = node;
    setVideoEl(node);
  }, []);

  const dragRef = useRef<{
    startX: number;
    startTime: number;
    pixelsPerSecond: number;
    pointerId: number;
    usingLowRes: boolean;
  } | null>(null);

  const pendingTargetRef = useRef<number | null>(null);
  const rafPendingRef = useRef(false);
  const seekInFlightRef = useRef(false);

  const [duration, setDuration] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [src, setSrc] = useState<string | null>(null);
  const [lowResSrc, setLowResSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Hotspots are optional and fixed at generation time: `null` means this run
  // was never asked for them (or was sourced from a mobile scan, which cannot
  // have them), and nothing in the viewer can change that.
  const [hotspots, setHotspots] = useState<HotspotsDocument | null>(null);
  const [showHotspots, setShowHotspots] = useState(true);
  const [openHotspot, setOpenHotspot] = useState<HotspotTrack | null>(null);

  const lowResAvailable = lowResSrc != null;
  const hasHotspots = !!hotspots && hotspots.hotspots.length > 0;

  useEffect(() => {
    setDuration(null);
    setCurrentTime(0);
    setIsReady(false);
    setIsDragging(false);
    setError(null);
    setHotspots(null);
    setOpenHotspot(null);
    pendingTargetRef.current = null;
    rafPendingRef.current = false;
    seekInFlightRef.current = false;
    dragRef.current = null;
  }, [jobId]);

  // Fetch the hotspot tracks (best-effort; absence => no overlay).
  useEffect(() => {
    if (hotspotsAvailable === false) return;
    const ac = new AbortController();
    fetchHotspots(jobId, ac.signal)
      .then((doc) => {
        if (!ac.signal.aborted) setHotspots(doc);
      })
      .catch(() => {
        // Overlay is optional; never surface a fetch failure as an error.
      });
    return () => ac.abort();
  }, [hotspotsAvailable, jobId]);

  // Fetch both videos as authenticated blob URLs. The low-res variant is
  // optional — `null` means the file doesn't exist for this job (older
  // jobs from before the variant landed) and the viewer drives the
  // high-res element throughout. We revoke object URLs on cleanup so a
  // long history-table session doesn't leak hundreds of MB of cached
  // video bytes.
  useEffect(() => {
    const ac = new AbortController();
    let highResUrl: string | null = null;
    let lowResUrl: string | null = null;

    setSrc(null);
    setLowResSrc(null);

    fetchScrubVideoBlobUrl(jobId, ac.signal)
      .then((url) => {
        if (ac.signal.aborted) {
          URL.revokeObjectURL(url);
          return;
        }
        highResUrl = url;
        setSrc(url);
      })
      .catch((ex: unknown) => {
        if (ac.signal.aborted) return;
        setError(
          ex instanceof Error ? `Could not load 360 video: ${ex.message}` : 'Could not load 360 video',
        );
      });

    fetchScrubVideoLowBlobUrl(jobId, ac.signal)
      .then((url) => {
        if (ac.signal.aborted) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        if (url) {
          lowResUrl = url;
          setLowResSrc(url);
        }
      })
      .catch(() => {
        // Low-res is best-effort; failures are silently ignored — the
        // viewer falls back to the high-res element.
      });

    return () => {
      ac.abort();
      if (highResUrl) URL.revokeObjectURL(highResUrl);
      if (lowResUrl) URL.revokeObjectURL(lowResUrl);
    };
  }, [jobId]);

  // Mirror `currentTime` into React state so the time readout below
  // updates as we scrub.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onTime = () => setCurrentTime(video.currentTime);
    const onLoaded = () => {
      setDuration(Number.isFinite(video.duration) ? video.duration : null);
      setIsReady(true);
    };
    const onError = () => {
      setError('Could not load 360 video');
      setIsReady(false);
    };

    video.addEventListener('timeupdate', onTime);
    // `seeked` keeps the overlay in step during a drag-scrub, where
    // `timeupdate` can lag behind the discrete seeks.
    video.addEventListener('seeked', onTime);
    video.addEventListener('loadedmetadata', onLoaded);
    video.addEventListener('canplay', onLoaded);
    video.addEventListener('error', onError);
    return () => {
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('seeked', onTime);
      video.removeEventListener('loadedmetadata', onLoaded);
      video.removeEventListener('canplay', onLoaded);
      video.removeEventListener('error', onError);
    };
  }, [videoEl]);

  // Entering the discrete viewer from continuous 360 mode can leave the
  // shared video element between samples. Snap it immediately so every frame
  // shown by this mode belongs to its configured uniform set.
  useEffect(() => {
    if (sampleCount == null || !duration) return;
    const video = videoRef.current;
    if (!video) return;

    const target = snapToUniformSample(video.currentTime, duration, sampleCount);
    if (Math.abs(video.currentTime - target) >= Number.EPSILON) {
      video.currentTime = target;
    }
    const lowRes = lowResVideoRef.current;
    if (lowRes && lowRes.readyState >= 1) lowRes.currentTime = target;
    setCurrentTime(target);
  }, [duration, sampleCount]);

  const scheduleSeek = useCallback(() => {
    if (rafPendingRef.current || seekInFlightRef.current) return;
    rafPendingRef.current = true;
    requestAnimationFrame(() => {
      rafPendingRef.current = false;
      const target = pendingTargetRef.current;
      pendingTargetRef.current = null;
      if (target == null) return;

      const drag = dragRef.current;
      const v = drag?.usingLowRes
        ? lowResVideoRef.current
        : videoRef.current;
      if (!v) return;
      const minStep = sampleCount == null ? MIN_SEEK_STEP_SECONDS : Number.EPSILON;
      if (Math.abs(v.currentTime - target) < minStep) return;

      seekInFlightRef.current = true;
      const onSeeked = () => {
        v.removeEventListener('seeked', onSeeked);
        seekInFlightRef.current = false;
        // Keep the hotspot overlay in step while dragging. During a
        // low-res drag the *low-res* element is the one seeking, so the
        // high-res element's own `seeked` listener never fires — mirror
        // the active element's time into state here so the markers track.
        setCurrentTime(v.currentTime);
        // A newer target may have arrived while we were waiting for
        // the seek to complete; chain another rAF to consume it.
        if (pendingTargetRef.current != null) scheduleSeek();
      };
      v.addEventListener('seeked', onSeeked);
      v.currentTime = target;
    });
  }, [sampleCount]);

  const beginDrag = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const video = videoRef.current;
      if (!video || !duration || duration <= 0) return;

      video.pause();
      const lowRes = lowResVideoRef.current;
      if (lowRes) lowRes.pause();

      // readyState >= 2 (HAVE_CURRENT_DATA) means the low-res element
      // has at least the current frame decoded — safe to display
      // without showing a black flash. If the low-res file is still
      // doing its initial HTTP fetch we just drive the high-res for
      // this drag; the next one will pick up the cheaper path.
      const canUseLow = !!(lowRes && lowResAvailable && lowRes.readyState >= 2);
      if (canUseLow && lowRes) {
        lowRes.currentTime = video.currentTime;
      }

      const width = containerRef.current?.clientWidth ?? 800;
      const pixelsPerSecond = Math.max(1, width / duration);
      dragRef.current = {
        startX: e.clientX,
        startTime: video.currentTime,
        pixelsPerSecond,
        pointerId: e.pointerId,
        usingLowRes: canUseLow,
      };
      pendingTargetRef.current = null;
      seekInFlightRef.current = false;
      rafPendingRef.current = false;

      setIsDragging(true);
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // Some browsers throw if the pointer was already lost (e.g. a
        // synthetic pointerdown from a flaky touchscreen) — harmless.
      }
    },
    [duration, lowResAvailable],
  );

  const updateDrag = useCallback(
    (clientX: number) => {
      const drag = dragRef.current;
      if (!drag || !duration) return;
      const dx = clientX - drag.startX;
      let next = drag.startTime + dx / drag.pixelsPerSecond;
      // Wrap so the user can keep dragging in either direction
      // without hitting an end-stop.
      next = ((next % duration) + duration) % duration;
      // Tiny epsilon away from the exact end keeps Safari from
      // snapping back to 0 instead of seeking.
      if (next >= duration) next = duration - 0.001;
      if (sampleCount != null) {
        next = snapToUniformSample(next, duration, sampleCount);
      }
      pendingTargetRef.current = next;
      scheduleSeek();
    },
    [duration, sampleCount, scheduleSeek],
  );

  const endDrag = useCallback(() => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    pendingTargetRef.current = null;

    const lowRes = lowResVideoRef.current;
    const hi = videoRef.current;
    if (drag.usingLowRes && lowRes && hi) {
      // Hand the final time to the high-res element. Wait for its
      // `seeked` before fading the low-res out, otherwise the user
      // briefly sees the previous high-res frame while the new one
      // decodes.
      const target = lowRes.currentTime;
      const minStep = sampleCount == null ? MIN_SEEK_STEP_SECONDS : Number.EPSILON;
      if (Math.abs(hi.currentTime - target) < minStep) {
        setIsDragging(false);
        return;
      }
      const onHiSeeked = () => {
        hi.removeEventListener('seeked', onHiSeeked);
        setIsDragging(false);
      };
      hi.addEventListener('seeked', onHiSeeked);
      hi.currentTime = target;
    } else {
      setIsDragging(false);
    }
  }, [sampleCount]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // Only the primary mouse button should start a drag (mouse-only
      // guard; touch/pen always have button === 0).
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      beginDrag(e);
    },
    [beginDrag],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragRef.current) return;
      updateDrag(e.clientX);
    },
    [updateDrag],
  );

  const onPointerEnd = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag) return;
      try {
        e.currentTarget.releasePointerCapture(drag.pointerId);
      } catch {
        // Already released by the browser (e.g. the element was
        // unmounted mid-drag); harmless.
      }
      endDrag();
    },
    [endDrag],
  );

  // ----- Download current frame ------------------------------------------
  // Captures the currently displayed *high-res* video frame to a PNG via
  // canvas. The button is disabled until the high-res element is ready,
  // so even when the user just released a drag we capture from the full-
  // resolution source rather than the 540p preview.

  const downloadCurrentFrame = useCallback(() => {
    const video = videoRef.current;
    if (!video || !duration) return;

    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    try {
      ctx.drawImage(video, 0, 0, w, h);
    } catch (ex) {
      // Most likely a tainted canvas (cross-origin video without CORS).
      console.error('Failed to draw video frame to canvas', ex);
      setError(
        ex instanceof Error
          ? `Could not capture frame: ${ex.message}`
          : 'Could not capture frame',
      );
      return;
    }

    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        const tSec = video.currentTime;
        const tag = `${tSec.toFixed(3).replace('.', '-')}s`;
        a.href = url;
        a.download = `${scanId ? `${scanId}_` : ''}frame_${tag}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        // Revoke after a tick so Chrome has time to start the download.
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    }, 'image/png');
  }, [duration, scanId]);

  if (error) {
    return (
      <div className="bg-black text-danger-300 text-sm p-6 text-center">
        {error}
      </div>
    );
  }

  const showLowResLayer = lowResAvailable && isDragging;

  return (
    <div className="bg-black relative select-none">
      <div
        ref={containerRef}
        className="relative w-full overflow-hidden cursor-ew-resize"
        // touch-action: none stops the browser from interpreting a
        // horizontal drag as a page scroll/swipe gesture, which would
        // otherwise compete with our pointer handlers on touch devices.
        style={{ touchAction: 'none' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
      >
        <video
          ref={setVideoNode}
          // Only mount a `src` once the blob URL is ready. Setting
          // `src=""` triggers an abort + error event in some browsers
          // and lights up the Could-not-load error banner spuriously.
          {...(src ? { src } : {})}
          controls={false}
          playsInline
          muted
          preload="auto"
          draggable={false}
          disablePictureInPicture
          disableRemotePlayback
          className={`w-full max-h-[75vh] mx-auto block object-contain pointer-events-none transition-opacity duration-75 ${showLowResLayer ? 'opacity-0' : 'opacity-100'}`}
        />
        {lowResAvailable && lowResSrc && (
          <video
            ref={lowResVideoRef}
            src={lowResSrc}
            controls={false}
            playsInline
            muted
            preload="auto"
            draggable={false}
            disablePictureInPicture
            disableRemotePlayback
            // Stacked exactly over the high-res element. Opacity-based
            // hide keeps it decoded so the swap on pointerdown is
            // instant — `display: none` would force a full reload.
            className={`absolute inset-0 w-full h-full object-contain pointer-events-none transition-opacity duration-75 ${showLowResLayer ? 'opacity-100' : 'opacity-0'}`}
          />
        )}
        {hotspots && (
          <HotspotOverlay
            doc={hotspots}
            videoEl={videoEl}
            currentTime={currentTime}
            enabled={showHotspots}
            onOpen={setOpenHotspot}
          />
        )}
        {(!src || !isReady) && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm text-white text-sm">
            Loading 360 video…
          </div>
        )}
        <HotspotModal jobId={jobId} hotspot={openHotspot} onClose={() => setOpenHotspot(null)} />
      </div>
      <div className="px-4 py-3 flex items-center justify-between gap-3 text-white/80 text-xs bg-black/60">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={downloadCurrentFrame}
            disabled={!isReady}
            className="px-2.5 py-1 rounded-md text-[11px] font-medium border border-white/30 text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            title="Capture the currently displayed video frame as a PNG"
          >
            Download current frame
          </button>
          {/* Hotspots are decided when the run is requested, so the only
              control here is visibility — there is nothing to generate. */}
          {hasHotspots && (
            <button
              type="button"
              onClick={() => setShowHotspots((v) => !v)}
              aria-pressed={showHotspots}
              className={`px-2.5 py-1 rounded-md text-[11px] font-medium border whitespace-nowrap transition-colors ${
                showHotspots
                  ? 'border-indigo-300/70 bg-indigo-500/30 text-white'
                  : 'border-white/30 text-white hover:bg-white/10'
              }`}
              title="Show or hide the interactive hotspots"
            >
              {showHotspots ? 'Hotspots: on' : 'Hotspots: off'}
            </button>
          )}
        </div>
        <div className="font-mono whitespace-nowrap">
          {duration
            ? `${sampleCount == null ? '' : `frame ${uniformSampleIndex(currentTime, duration, sampleCount) + 1}/${sampleCount} · `}${currentTime.toFixed(2)}s / ${duration.toFixed(2)}s`
            : '\u2014'}
        </div>
        <div className="opacity-70 whitespace-nowrap">
          {sampleCount == null
            ? 'drag to rotate manually'
            : `drag through ${sampleCount} sampled frames`}
        </div>
      </div>
    </div>
  );
}
